/**
 * Utility functions for inventory management and stock material operations
 * These functions help with stock action classification, data processing, and business logic
 */

/**
 * Checks if a stock action is a group assignment matching the given pieces
 * Group assignments have multiple pieces that match exactly with the provided pieces array
 *
 * @param {Object} stockAction - The stock action to check
 * @param {string[]} pieces - Array of piece URIs to match against
 * @returns {boolean} True if this is a group assignment for the given pieces
 */
export const isGroupAssignment = (stockAction, pieces) => {
  const actionPieces = stockAction.metadata?.pieces || [];
  return actionPieces.length > 1 && actionPieces.length === pieces.length;
};

/**
 * Checks if a stock action is an individual assignment for a specific piece
 * Individual assignments have exactly one piece in their metadata
 *
 * @param {Object} stockAction - The stock action to check
 * @param {string|null} pieceUri - Optional specific piece URI to match against
 * @returns {boolean} True if this is an individual assignment (optionally for specific piece)
 */
export const isIndividualAssignment = (stockAction, pieceUri = null) => {
  const actionPieces = stockAction.metadata?.pieces || [];
  if (actionPieces.length !== 1) return false;
  return pieceUri ? actionPieces[0] === pieceUri : true;
};

/**
 * Extracts unique stock URIs from relationships by finding their corresponding stock actions
 * This is used to get stock URIs from piece relationships (inherited from group assignments)
 *
 * @param {Object[]} relationships - Array of stock action relationships
 * @param {Object[]} stockActions - Array of all stock actions to search through
 * @returns {Set} Set of unique stock URIs found in the relationships
 */
export const getStockUrisFromRelationships = (relationships, stockActions) => {
  const stockUris = new Set();
  relationships.forEach(rel => {
    const stockAction = stockActions.find(sa => sa.uri === rel.stock_action);
    if (stockAction) {
      stockUris.add(stockAction.source_stock);
    }
  });
  return stockUris;
};

/**
 * Filters stock actions to find those that match specific criteria
 * Utility function for finding stock actions by source stock and custom filters
 *
 * @param {Object[]} stockActions - Array of stock actions to filter
 * @param {string} sourceStockUri - The source stock URI to match
 * @param {Function} filterFn - Additional filter function (defaults to returning true)
 * @returns {Object[]} Filtered array of stock actions
 */
export const findStockActionsByStock = (stockActions, sourceStockUri, filterFn = () => true) => {
  return stockActions.filter(action => action.source_stock === sourceStockUri && filterFn(action));
};

/**
 * Calculates the total quantity from an array of relationships
 * Used to sum up quantities from multiple stock action relationships
 *
 * Uses Math.abs because relationship.quantity is a signed delta (negative for consume actions).
 *
 * @param {Object[]} relationships - Array of relationships with quantity properties
 * @returns {number} Total quantity sum
 */
export const calculateTotalQuantityFromRelationships = relationships => {
  return relationships.reduce((sum, rel) => sum + Math.abs(rel.quantity || 0), 0);
};

/**
 * Groups stock actions by their assignment type (individual vs group)
 * Useful for separating and processing different types of assignments
 *
 * @param {Object[]} stockActions - Array of stock actions to group
 * @returns {Object} Object with 'individual' and 'group' arrays
 */
export const groupStockActionsByType = stockActions => {
  const individual = [];
  const group = [];

  stockActions.forEach(action => {
    const actionPieces = action.metadata?.pieces || [];
    if (actionPieces.length === 1) {
      individual.push(action);
    } else if (actionPieces.length > 1) {
      group.push(action);
    }
  });

  return { individual, group };
};

/**
 * Finds pieces within a group that have individual assignments
 * Used for detecting mixed assignment scenarios and showing warnings
 *
 * @param {Object[]} stockActions - Array of all stock actions
 * @param {string[]} groupPieces - Array of piece URIs in the group
 * @returns {string[]} Array of piece URIs that have individual assignments
 */
export const findPiecesWithIndividualAssignments = (stockActions, groupPieces) => {
  const piecesWithIndividual = new Set();

  stockActions.forEach(action => {
    if (isIndividualAssignment(action)) {
      const actionPieces = action.metadata?.pieces || [];
      if (groupPieces.includes(actionPieces[0])) {
        piecesWithIndividual.add(actionPieces[0]);
      }
    }
  });

  return Array.from(piecesWithIndividual);
};
